import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RefreshCw, Filter, ClipboardList, User, Tag, Store, Inbox, Settings } from "lucide-react";

const ACTION_GROUPS = [
  { value: "",           label: "Toutes actions" },
  { value: "offer",      label: "Offres" },
  { value: "business",   label: "Partenaires" },
  { value: "request",    label: "Demandes" },
  { value: "settings",   label: "Paramètres" },
];
const iconFor = (a) => {
  if (a.startsWith("offer")) return <Tag size={14} className="text-emerald-600"/>;
  if (a.startsWith("business")) return <Store size={14} className="text-blue-600"/>;
  if (a.startsWith("request")) return <Inbox size={14} className="text-rose-600"/>;
  if (a.startsWith("settings")) return <Settings size={14} className="text-slate-600"/>;
  return <ClipboardList size={14} className="text-slate-400"/>;
};
const badgeFor = (a) => {
  if (a.includes("delete")) return "bg-rose-100 text-rose-700";
  if (a.includes("publish") || a.endsWith("published")) return "bg-emerald-100 text-emerald-700";
  if (a.includes("reject")) return "bg-orange-100 text-orange-700";
  if (a.includes("update") || a.includes("status")) return "bg-blue-100 text-blue-700";
  return "bg-slate-100 text-slate-700";
};

export default function AdminJournal() {
  const [rows, setRows] = useState([]);
  const [actor, setActor] = useState("");
  const [group, setGroup] = useState("");
  const [limit, setLimit] = useState(200);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("limit", String(limit));
      if (actor.trim()) params.set("actor", actor.trim());
      if (group) params.set("action", group);
      const { data } = await api.get(`/admin/activity-log?${params.toString()}`);
      setRows(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [group, limit]);

  const actors = useMemo(() => [...new Set(rows.map((r) => r.actor_email).filter(Boolean))].slice(0, 20), [rows]);

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold" data-testid="admin-journal-title">Journal d'activité</h1>
          <p className="text-slate-500 text-xs md:text-sm mt-1">Trace de toutes les actions internes (créations, modifications, modérations, suppressions). Non modifiable.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="relative">
            <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
            <Input placeholder="Filtrer par email admin…" value={actor} onChange={(e)=>setActor(e.target.value)}
                   onKeyDown={(e)=>{ if(e.key==="Enter") load(); }}
                   className="pl-9 w-full sm:w-64" data-testid="journal-actor-filter"/>
          </div>
          <Select value={group || "all"} onValueChange={(v)=>setGroup(v==="all"?"":v)}>
            <SelectTrigger className="w-44" data-testid="journal-group-filter">
              <span className="inline-flex items-center gap-2"><Filter size={12}/><SelectValue placeholder="Type"/></span>
            </SelectTrigger>
            <SelectContent>
              {ACTION_GROUPS.map((g)=>(<SelectItem key={g.value||"all"} value={g.value||"all"}>{g.label}</SelectItem>))}
            </SelectContent>
          </Select>
          <Select value={String(limit)} onValueChange={(v)=>setLimit(parseInt(v,10))}>
            <SelectTrigger className="w-28" data-testid="journal-limit"><SelectValue/></SelectTrigger>
            <SelectContent>
              {[100, 200, 500, 1000].map((n)=>(<SelectItem key={n} value={String(n)}>{n} lignes</SelectItem>))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load} disabled={loading} data-testid="journal-refresh"><RefreshCw size={14}/></Button>
        </div>
      </div>

      {actors.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2 text-xs">
          {actors.map((a) => (
            <button key={a} onClick={()=>{ setActor(a); load(); }}
                    className="px-2 py-1 rounded-full bg-slate-100 text-slate-700 hover:bg-slate-200">
              {a}
            </button>
          ))}
        </div>
      )}

      <div className="mt-4 bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="p-3 text-left w-40">Date</th>
              <th className="p-3 text-left">Action</th>
              <th className="p-3 text-left">Cible</th>
              <th className="p-3 text-left">Acteur</th>
              <th className="p-3 text-left">Détails</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`journal-row-${r.id}`}>
                <td className="p-3 whitespace-nowrap text-xs text-slate-500">
                  {new Date(r.created_at).toLocaleString("fr-FR")}
                </td>
                <td className="p-3">
                  <div className="inline-flex items-center gap-2">
                    {iconFor(r.action || "")}
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${badgeFor(r.action || "")}`}>{r.action}</span>
                  </div>
                </td>
                <td className="p-3 text-xs">
                  {r.target_type ? <span className="text-slate-500">{r.target_type}</span> : "—"}
                  {r.target_id ? <div className="font-mono text-[10px] text-slate-400 truncate max-w-[180px]">{r.target_id}</div> : null}
                </td>
                <td className="p-3 text-xs">
                  <div className="font-semibold">{r.actor_email || "—"}</div>
                  {r.actor_role && <div className="text-slate-400">{r.actor_role}</div>}
                </td>
                <td className="p-3 text-xs">
                  {r.meta && Object.keys(r.meta).length > 0 ? (
                    <code className="text-[10px] text-slate-600 bg-slate-50 px-2 py-1 rounded">
                      {Object.entries(r.meta).map(([k,v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : String(v).slice(0,60)}`).join(" · ")}
                    </code>
                  ) : "—"}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan="5" className="p-6 text-center text-slate-500">Aucune action enregistrée pour ces filtres.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
